<template>
  <div class="wserver-baseball-page">
    <!-- 상단 헤더 -->
    <div class="page-header">
      <div class="header-left">
        <h1>⚾ Wserver 숫자야구</h1>
        <span class="live-badge">LIVE</span>
      </div>
      <div class="header-right">
        <a href="https://geek9.kr/sosoceo" target="_blank" class="join-link">
          👉 게임 참여하기
        </a>
      </div>
    </div>
    <div class="main-layout" :key="mainKey">
      <!-- LEFT: GAME SCREEN -->
      <div class="game-panel">
        <div class="panel-header">
          <h2>🎮 게임 화면</h2>
          <div v-if="currentRecord.id && gameState !== 'ready'" class="current-player">
            <span class="record-badge">성공:{{ currentRecord.wins }} 실패:{{ currentRecord.losses }} 평균:{{ currentRecord.aver }} 승률:{{ currentRecord.rate }}%</span>
            <span class="player-badge">👤 {{ maskId(currentRecord.id) }}</span>
          </div>
        </div>

        <div class="game-content">
          <!-- IDLE: 대기자 없음 -->
          <div v-if="gameState === 'ready' && waitingList.length === 0" class="idle-state">
            <div class="idle-content">
              <div class="baseball-icon">⚾</div>
              <h3>게임에 도전하세요!</h3>
              <p class="idle-desc">
                대기 중인 플레이어가 없습니다.<br><br>
                <strong>https://geek9.kr/sosoceo</strong> 에서<br>
                <strong>3자리 숫자</strong>를 입력해보세요!
              </p>
              <div class="idle-hint">
                💡 정답을 맞히면 기록이 랭킹에 반영됩니다.
              </div>
            </div>
          </div>

          <!-- 다음 플레이어 대기 중 -->
          <div v-else-if="gameState === 'ready' && waitingList.length > 0" class="idle-state next-player-waiting">
            <div class="idle-content">
              <div class="baseball-icon">⏳</div>
              <h3>다음 플레이어 게임 준비 중...</h3>
              <p class="idle-desc">
                <strong>{{ maskEmail(waitingList[0].id) }}</strong> 님이<br>
                곧 게임을 시작합니다.
              </p>
              <div class="idle-hint">
                게임이 시작되면 큰 안내 팝업이 표시됩니다.
              </div>
            </div>
          </div>

          <!-- ACTIVE GAME -->
          <div v-else class="active-game-state">
            <div class="guess-log-container" ref="logContainer">
              <div class="log-header">
                <span>📜 <strong>시도 기록 (최근 {{ guessHistory.length }}회)</strong></span>
                <span class="attempts-badge">{{ gameData.attempts }} / 10</span>
              </div>
              <div v-if="guessHistory.length > 0" class="guess-log">
                <div
                  v-for="(entry, index) in guessHistory"
                  :key="index"
                  class="log-row"
                  :class="{ 'latest': index === guessHistory.length - 1 }"
                >
                  <div class="guess-number">{{ entry.guess }}</div>
                  <div class="result-badges">
                    <span v-if="entry.s > 0" class="badge strike">{{ entry.s }}S</span>
                    <span v-if="entry.b > 0" class="badge ball">{{ entry.b }}B</span>
                    <span v-if="entry.s === 0 && entry.b === 0" class="badge out">OUT</span>
                  </div>
                </div>
              </div>
              <div v-else class="log-empty">첫 번째 숫자를 기다리는 중...</div>
            </div>

            <div v-if="gameState === 'active'" class="timer-section">
              <div class="timer-header">
                <span class="timer-label">⏱ 다음 입력까지 남은 시간</span>
                <span class="timer-value" :class="{ danger: timeLeft < 10 }">{{ Math.ceil(timeLeft) }}초</span>
              </div>
              <div class="progress-bar-wrapper">
                <div class="progress-bar" :style="{ width: (timeLeft / 30 * 100) + '%' }" :class="{ danger: timeLeft < 10 }"></div>
              </div>
              <div class="input-prompt">
                <p>🔢 <strong>3자리 숫자</strong>를 입력해주세요!</p>
                <p class="sub-prompt">결과를 보고 30초 안에 다음 숫자를 입력하세요.</p>
              </div>
            </div>


          </div>
        </div>

        <div class="game-footer">
          <div class="stat-item"><span class="label">현재 시도</span><span class="value">{{ gameData.attempts }} / 10</span></div>
          <div class="stat-item"><span class="label">진행 시간</span><span class="value">{{ formatTime(gameData.time) }}</span></div>
          <div class="stat-item" v-if="gameState === 'active'"><span class="label">{{ $t('menu.status') }}</span><span class="value status-active">진행 중</span></div>
        </div>


        <div v-if="gameState === 'win'" class="game-start-popup-overlay">
          <div class="game-start-popup" @click.stop>
            <div class="win-state">
              <div class="win-animation">🎉</div>
              <h3 class="win-title">정답입니다!!</h3>
              <p class="win-message">🏆 <strong>{{ popupPlayerId }}</strong></p>
              <div class="winner-info">
                <span>"{{ this.target }}"</span><br>
                <span class="attempts">시도 횟수: {{ gameData.attempts }}회</span>
              </div>
              <div class="win-message">
                <span class="attempts">{{ win }}승 {{ loss }}패</span><br>
                <span class="attempts">평균: {{ parseFloat(aver).toFixed(2) }}</span><br>
                <span class="attempts">승률: {{ parseFloat(rate).toFixed(2) }}%</span><br>
              </div>
              
              <p class="next-info">잠시 후 새로운 게임이 시작됩니다...</p>
            </div>
          </div>
        </div>
        <!-- WIN -->

        <!-- GAME OVER -->
        <div v-else-if="gameState === 'gameover'" class="game-start-popup-overlay">
          <div class="game-start-popup" @click.stop>
            <div class="gameover-state">
              <div class="gameover-icon">😢</div>
              <h3 class="gameover-title">게임 오버</h3>
              <p class="gameover-reason" v-if="gameoverReason === 'timeout'">30초 내에 입력이 없었습니다.</p>
              <p class="gameover-reason" v-else-if="gameoverReason === 'max_attempts'">10회 시도 제한에 도달했습니다.</p>
              <p class="gameover-reason" v-else-if="gameoverReason === '플레이어 접속 종료'">플레이어가 접속을 종료했습니다.</p>
              <div v-if="this.target" class="answer-box">
                <span class="answer-label">정답</span>
                <span class="answer-number">"{{ this.target }}"</span>
              </div>
              <div v-else-if="gameData.answer" class="answer-box">
                <span class="answer-label">정답</span>
                <span class="answer-number">"{{ gameData.answer }}"</span>
              </div>

              <p class="gameover-sub">아쉽지만 다음 기회에 도전하세요!</p>
            </div>
          </div>
        </div>
        <!-- GAME START POPUP -->
        <div v-else-if="showStartPopup" class="game-start-popup-overlay">
          <div class="game-start-popup" @click.stop>
            <div class="popup-icon">🎮</div>
            <h2>게임이 시작되었습니다!</h2>
            <Divider/>
            <p class="popup-player"><strong>{{ popupPlayerId }}</strong> 님</p>
            <p class=""><strong>성공</strong> {{ win }} </p>
            <p class=""><strong>실패</strong> {{ loss }} </p>
            <p class=""><strong>승률</strong> {{ rate }}% </p>
            <p class=""><strong>평균</strong> {{ aver }}</p>
            <Divider/>
            <p class="popup-message">3자리 숫자를<br>입력해주세요!</p>
          </div>
        </div>
      </div>

      <!-- RIGHT: RANKING -->
      <!-- <div class="ranking-panel">
        <div class="panel-header"><h2>🏆 랭킹</h2></div>
        <div class="ranking-tabs">
          <button @click="activeTab = 'winrate'" :class="{ active: activeTab === 'winrate' }">승률 TOP</button>
          <button @click="activeTab = 'wins'" :class="{ active: activeTab === 'wins' }">승수 TOP</button>
        </div>
        <div class="ranking-list">
          <div v-for="(user, index) in sortedRankings" :key="index" class="rank-row" :class="{ 'top-3': user.rank <= 3 }">
            <div class="rank-number">{{ user.rank }}위</div>
            <div class="user-info">
              <span class="user-id">{{ user.id }}</span>
              <div class="record-detail">{{ user.wins }}승 {{ user.losses }}패</div>
            </div>
            <div class="rank-value">
              <span v-if="activeTab === 'winrate'" class="winrate">{{ user.winRate }}<small>%</small></span>
              <span v-else class="wins">{{ user.wins }}<small>승</small></span>
            </div>
          </div>
        </div>
        <div class="ranking-footer"><small>※ 성공률 = 승수 / (승수 + 실패수) 기준<br>실시간 DB 연동 예정</small></div>
      </div> -->
    </div> 
    <!-- BOTTOM: WAITING LIST -->
    <div class="waiting-panel">
      <div class="waiting-header">
        <h3>📋 대기 인원 : <span class="count">{{ waitingList.length }}명</span></h3>
        <span class="update-hint">실시간 업데이트</span>
      </div>
      <div class="waiting-content">
        <div v-if="waitingList.length > 0" class="waiting-grid">
          <div v-for="(user, index) in waitingList" :key="index" class="waiting-card" :class="{ 'next-player': index === 0 }">
            <span class="order">{{ index + 1 }}</span>
            <span class="masked-email">{{ maskEmail(user.id) }}</span>
            <span v-if="index === 0" class="next-badge">NEXT UP</span>
          </div>
        </div>
        <div v-else class="waiting-empty">
          <p>현재 대기 중인 플레이어가 없습니다.</p>
          <p class="empty-sub">https://geek9.kr/sosoceo 에서 게임에 도전해 보세요!</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { io } from 'socket.io-client'

const page = 'wserver'

export default {
  name: page,
  emits: ["setMenu", "login", "logout"],
  setup() {
    const currentRecord = ref({ id:'', wins: 0, losses: 0, aver: 0.0, rate: 0.0 })
    return {
      currentRecord
    }
  },
  data() {
    return {
      mainKey:-1,
      playingPlayerId: null,
      playedPlayers: new Set(),
      socket: null,
      socketId: '',
      target: '',
      win: '',
      loss: '',
      aver: '',
      rate: '',
      gameState: 'ready',
      gameData: { id: '', message: '', s: 0, b: 0, attempts: 0, time: 0, answer: '' },
      guessHistory: [],
      timeLeft: 30,
      progressTimer: null,
      gameoverReason: '',
      turnStartTime: null,
      
      waitingList: [],
      // activeTab: 'winrate',
      // rankings: [],
      showStartPopup: false,
      popupPlayerId: undefined
    }
  },

  computed: {
    // sortedRankings() {
    //   let sorted = [...this.rankings]
    //   if (this.activeTab === 'winrate') sorted.sort((a, b) => b.winRate - a.winRate)
    //   else sorted.sort((a, b) => b.wins - a.wins)
    //   return sorted.map((user, index) => ({ ...user, rank: index + 1 }))
    // }
  },

  watch: {
    waitingList(newList, oldList) {
      console.log('this.gameState : ' + this.gameState)
      console.log('this.showStartPopup : ' + this.showStartPopup)
      if (this.gameState === 'ready' && newList.length > 0 && !this.showStartPopup) {
        if (!oldList || oldList.length === 0 || oldList[0] !== newList[0]) {
          this.showNextPlayerPopup(newList[0])
        }
      }
    }
  },

  mounted() {
    // if( this.api.getCookies('mail') !== 'geek9@kakao.com' ) {
    //   const link = document.createElement('a')
    //   link.href = 'https://geek9.kr/sosoceo?page=baseball'
    //   document.body.appendChild(link)
    //   link.click()
    //   return
    // }
    this.api.setPage(page)
    this.$emit("setMenu", { page: page })
    this.socket = io(this.api.BACKEND_URL, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
    })

    this.socket.on('connect', () => {
      this.socketId = this.socket.id
      this.socket.emit('sendMessage', { id: this.socketId, page: this.$route.query.page || 'baseball' })
    })

    this.socket.on('baseballResult', (data) => {
      if(!this.popupPlayerId) return
      const isNewGame = !this.gameData.id || this.gameData.id !== data.id
      if (data.id) this.removeFromWaitingList(data.id)
      if (isNewGame) {
        this.guessHistory = []
        this.gameoverReason = ''
        this.stopTurnTimer()
        this.currentRecord = { id:'', wins: 0, losses: 0, aver: 0.0, rate: 0.0 }
        if (data.id) this.removeFromWaitingList(data.id)
      }
      this.target = data.target
      this.gameData = { ...data }
      this.guessHistory.push({ guess: data.message, s: data.s, b: data.b })

      if (this.showStartPopup && this.guessHistory.length === 1) {
        setTimeout(() => { if (this.showStartPopup) this.showStartPopup = false }, 600)
      }

      this.$nextTick(() => {
        const container = this.$refs.logContainer
        if (container) container.scrollTop = container.scrollHeight
      })

      this.gameState = 'active'

      if (this.guessHistory.length >= 10) {
        this.stopTurnTimer()
        this.timeLeft = 0
        setTimeout(() => { if (this.gameState !== 'win') this.handleGameOver('max_attempts') }, 3200)
      } else {
        this.startTurnTimer()
      }
    })

    this.socket.on('baseballWin', (data) => {

      if (!this.gameData.id || this.gameData.id !== data.id) {
        if (data.id) this.removeFromWaitingList(data.id)
      }
      this.win = data.win
      this.loss = data.loss
      this.aver = parseFloat(data.aver).toFixed(2)
      this.rate = parseFloat(this.win / (this.win + this.loss) * 100).toFixed(2)
      this.target = data.target
      this.gameData = { ...data }
      this.guessHistory.push({ guess: data.message, s: 3, b: 0 })

      if (this.showStartPopup) {
        setTimeout(() => { 
          if (this.showStartPopup) this.showStartPopup = false 
          this.clearUser()
        }, 1000)
      }

      this.currentRecord.wins += 1
      this.refreshcurrentRecord()
      this.gameState = 'win'
      this.stopTurnTimer()
      this.timeLeft = 0
      setTimeout(() => this.resetGame(), 5500)
    })

    this.socket.on('baseballReady', () => {
      console.log('baseballReady')
      if (this.gameState === 'active' || this.gameState === 'win' || this.gameState === 'gameover') {
        // if(this.gameState === 'win' || this.gameState === 'gameover') this.clearUser()
        return
      }

      this.stopTurnTimer()
      this.timeLeft = 30
      this.gameState = 'ready'
      this.clearUser()
      
      if (this.waitingList.length > 0 && !this.showStartPopup) {
        this.startNextPlayerTurn()
      }
    })

    this.socket.on('removeWaitingQueue', (data) => {
      console.log('@@@@@@@@@@@@@ data')
      const id = data.id
      if (this.gameState === 'active' && this.gameData.id === id) {
        this.stopTurnTimer() 
        this.handleGameOver('플레이어 접속 종료') 
      } 
      this.removeFromWaitingList(id)
    })

    this.socket.on('updateWaitingQueue', (data) => {
      const id = data.id
      // this.currentRecord = { id:id, wins: Number(data.win), losses: Number(data.loss), rate:parseFloat(data.rate), aver:parseFloat(data.aver) }
      
      // this.mainKey++
      if (id && !this.waitingList.some(player => player.id === id) && id !== this.playingPlayerId) {
        this.waitingList.push({ id:id, wins: Number(data.win), losses: Number(data.loss), rate:parseFloat(data.rate), aver:parseFloat(data.aver) })
        if (this.gameState === 'ready' && !this.showStartPopup) {
          this.startNextPlayerTurn()
        }  
      }
    })

    this.socket.on('disconnect', (reason) => console.warn('[Wserver] disconnect:', reason))
    this.socket.on('reconnect', () => {
      this.socket.emit('sendMessage', { id: this.socket.id, page: this.$route.query.page || 'baseball' })
    })
  },

  beforeUnmount() {
    if (this.socket) {
      this.socket.off('baseballResult')
      this.socket.off('baseballWin')
      this.socket.off('baseballReady')
      this.socket.off('updateWaitingQueue')
      this.socket.disconnect()
    }
    this.stopTurnTimer()
  },

  methods: {
    showNextPlayerPopup(player) {
      // console.log('##### showNextPlayerPopup #####')
      // console.log('playerId : ' + playerId)
      // console.log('this.showStartPopup : ' + this.showStartPopup)
      // console.log('this.gameState : ' + this.gameState)
      if (!player.id || this.showStartPopup || this.gameState !== 'ready') return
      this.setUser({id:player.id})
      this.showStartPopup = true
      this.popupPlayerId = this.maskEmail(player.id)
      // console.log('######################')
      // console.log(player)
      // { id:id, wins: Number(data.win), losses: Number(data.loss), rate:parseFloat(data.rate), aver:parseFloat(data.aver) }
      this.win = player.wins
      this.loss = player.losses
      this.aver = player.aver
      this.rate = this.win / (this.win + this.loss) * 100
      
      setTimeout(() => {
        if (this.showStartPopup && this.gameState === 'ready') {
          this.showStartPopup = false
        }
      }, 15000)
    },

    removeFromWaitingList(playerId) {
      if (!playerId) return
      const pid = String(playerId).trim()
      const shortPid = pid.substring(0, 8)
      this.waitingList = this.waitingList.filter(item => {
        const itemStr = String(item.id).trim()
        return !(itemStr === pid || itemStr.includes(shortPid) || shortPid.includes(itemStr) || itemStr.includes(pid.substring(0, 6)))
      })
    },

    clearUser() {
      this.socket.emit('clearUser')
    },

    setUser(param) {
      this.socket.emit('setUser', param)
    },

    startNextPlayerTurn() {
      if (this.waitingList.length === 0) return
      const nextPlayer = this.waitingList.shift()
      this.setUser({id:nextPlayer.id})
      console.log(`[Game] 게임 시작: ${nextPlayer.id}. 대기열에서 제거됨.`)
      
      this.playingPlayerId = nextPlayer.id
      this.win = nextPlayer.wins
      this.loss = nextPlayer.losses
      this.aver = parseFloat(nextPlayer.aver).toFixed(2)
      this.rate = parseFloat(this.win / (this.win + this.loss) * 100).toFixed(2)

      this.gameState = 'active'
      this.gameData = { id: nextPlayer, message: '', s: 0, b: 0, attempts: 0, time: 0, answer: '' }
      this.guessHistory = []
      // this.currentRecord = { id:'', wins: 0, losses: 0, aver:0.0, rate:0.0 }
      this.gameoverReason = ''

      this.showStartPopup = true
      this.popupPlayerId = this.maskEmail(nextPlayer.id)
      this.startTurnTimer()
    },

    stopTurnTimer() {
      if (this.progressTimer) { 
        clearInterval(this.progressTimer) 
        this.progressTimer = null 
      }
      this.turnStartTime = null
    },

    startTurnTimer() {
      this.stopTurnTimer()
      this.turnStartTime = Date.now()
      this.timeLeft = 30
      this.progressTimer = setInterval(() => {
        if (!this.turnStartTime || this.gameState !== 'active') { 
          this.stopTurnTimer() 
          return 
        }
        const elapsed = (Date.now() - this.turnStartTime) / 1000
        this.timeLeft = Math.max(0, 30 - elapsed)
        if (this.timeLeft <= 0) { 
          this.stopTurnTimer() 
          this.handleGameOver('timeout') 
        }
      }, 100)
    },

    refreshcurrentRecord() {
      // this.currentRecord = { wins: 0, losses: 0, aver: 0.0, rate: 0.0 }
      // guessHistory.length
      this.currentRecord.rate = parseFloat(this.currentRecord.wins / (this.currentRecord.wins + this.currentRecord.losses) * 100).toFixed(2) 
      
      
    },

    handleGameOver(reason) {
      this.api.axiosGql('UPSERT_BASEBALL', {type:'UPSERT_BASEBALL', id:this.playingPlayerId, page:'baseball', val1:0, val2:1, val3:10})
      this.clearUser()
      
      // ★ 핵심 수정: 팝업이 떠 있는 상태에서 30초 타임아웃 시 팝업 닫기 + 게임오버
      this.showStartPopup = false
      this.popupPlayerId = ''
      
      this.gameoverReason = reason
      this.gameState = 'gameover'
      this.stopTurnTimer()
      this.timeLeft = 0
      
      if (reason === 'timeout' || reason === 'max_attempts' || reason === '플레이어 접속 종료') {
        this.currentRecord.losses += 1
        this.refreshcurrentRecord()
      }

      this.playingPlayerId = null

      const resetDelay = (reason === 'timeout' && this.waitingList.length > 0) ? 2200 : 5500
      setTimeout(() => this.resetGame(), resetDelay)
    },

    resetGame() {
      if (this.gameState === 'active' || this.showStartPopup) return

      this.gameState = 'ready'
      this.guessHistory = []
      this.gameData = { id: '', message: '', s: 0, b: 0, attempts: 0, time: 0, answer: '' }
      this.timeLeft = 30
      this.gameoverReason = ''
      this.turnStartTime = null
      this.stopTurnTimer()
      this.playingPlayerId = null
      this.clearUser()
      if (this.waitingList.length > 0 && !this.showStartPopup) {
        const nextPlayer = this.waitingList.shift()
        this.showNextPlayerPopup(nextPlayer)
        // this.showNextPlayerPopup(this.waitingList[0])
      }
    },

    hideStartPopup() {
      this.showStartPopup = false
    },

    formatTime(seconds) {
      const m = Math.floor(seconds / 60).toString().padStart(2, '0')
      const s = (seconds % 60).toString().padStart(2, '0')
      return `${m}:${s}`
    },

    maskId(id) {
      if (!id) return '익명'
      return id.length > 6 ? id.substring(0, 4) + '***' : id + '***'
    },

    maskEmail(email) {
      if (!email) return ''
      const parts = String(email).split('@')
      if (parts.length !== 2) return email
      return `${parts[0]}`
      // return `${parts[0]}@***`
    }
  }
}
</script>


<style scoped>
/* =====================================================
   모든 스타일은 .wserver-baseball-page 하위 태그에만 적용
   ===================================================== */

.wserver-baseball-page {
  width: 100%;
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%);
  color: #fff;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  padding: 16px;
  box-sizing: border-box;
}

/* Header */
.wserver-baseball-page .page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #2a2a4a;
}

.wserver-baseball-page .header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.wserver-baseball-page .header-left h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  background: linear-gradient(90deg, #ffd700, #ffaa00);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.wserver-baseball-page .live-badge {
  background: #ff3b5c;
  color: white;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 20px;
  font-weight: 700;
  letter-spacing: 1px;
}

.wserver-baseball-page .join-link {
  background: #00d4ff;
  color: #000;
  padding: 8px 18px;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s;
}

.wserver-baseball-page .join-link:hover {
  background: #00b8e0;
  transform: translateY(-1px);
}

/* Main Layout */
.wserver-baseball-page .main-layout {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.wserver-baseball-page .game-panel {
  flex: 1.6;
  background: #16162b;
  border-radius: 16px;
  border: 1px solid #2a2a4a;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 620px;
  position: relative;
}

.wserver-baseball-page .panel-header {
  padding: 16px 20px;
  background: #1f1f3a;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.wserver-baseball-page .panel-header h2 {
  margin: 0;
  font-size: 20px;
  color: #ffd700;
}

.wserver-baseball-page .current-player {
  display: flex;
  align-items: center;
  gap: 8px;
}

.wserver-baseball-page .player-badge {
  background: #2a2a4a;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 14px;
}

.wserver-baseball-page .record-badge {
  background: #3a2a1f;
  color: #ffcc66;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
}

/* Game Content */
.wserver-baseball-page .game-content {
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
}

/* Idle State */
.wserver-baseball-page .idle-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.wserver-baseball-page .idle-content {
  max-width: 380px;
}

.wserver-baseball-page .baseball-icon {
  font-size: 72px;
  margin-bottom: 16px;
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-15px); }
}

.wserver-baseball-page .idle-state h3 {
  font-size: 26px;
  margin: 0 0 12px;
  color: #ffd700;
}

.wserver-baseball-page .idle-desc {
  color: #aaa;
  line-height: 1.6;
  margin-bottom: 20px;
}

.wserver-baseball-page .idle-hint {
  background: #1f1f3a;
  padding: 12px;
  border-radius: 10px;
  font-size: 14px;
  color: #ccc;
}

.wserver-baseball-page .next-player-waiting .baseball-icon {
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

/* Active Game State */
.wserver-baseball-page .active-game-state {
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Guess Log */
.wserver-baseball-page .guess-log-container {
  flex: 0 0 auto;
  background: #0f0f23;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 16px;
  border: 1px solid #2a2a4a;
  max-height: none;
  overflow-y: visible;
}

.wserver-baseball-page .log-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 8px 8px;
  font-size: 13px;
  color: #888;
  border-bottom: 1px solid #2a2a4a;
  margin-bottom: 8px;
}

.wserver-baseball-page .attempts-badge {
  background: #3a2a1f;
  color: #ffaa00;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
}

.wserver-baseball-page .guess-log {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.wserver-baseball-page .log-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #1a1a2e;
  padding: 10px 14px;
  border-radius: 8px;
  transition: all 0.2s;
}

.wserver-baseball-page .log-row.latest {
  background: #2a2a3f;
  border: 1px solid #ffd700;
  box-shadow: 0 0 8px rgba(255, 215, 0, 0.3);
}

.wserver-baseball-page .guess-number {
  font-family: 'Courier New', monospace;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 4px;
  color: #fff;
}

.wserver-baseball-page .result-badges {
  display: flex;
  gap: 6px;
}

.wserver-baseball-page .badge {
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
}

.wserver-baseball-page .badge.strike {
  background: #ff3b5c;
  color: white;
}

.wserver-baseball-page .badge.ball {
  background: #00b8ff;
  color: white;
}

.wserver-baseball-page .badge.out {
  background: #555;
  color: #ddd;
}

.wserver-baseball-page .log-empty {
  text-align: center;
  color: #666;
  padding: 40px 0;
  font-size: 15px;
}

/* Timer */
.wserver-baseball-page .timer-section {
  background: #1f1f3a;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 12px;
}

.wserver-baseball-page .timer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.wserver-baseball-page .timer-label {
  color: #aaa;
  font-size: 14px;
}

.wserver-baseball-page .timer-value {
  font-size: 28px;
  font-weight: 800;
  color: #00ff9d;
  font-variant-numeric: tabular-nums;
}

.wserver-baseball-page .timer-value.danger {
  color: #ff3b5c;
  animation: pulse 1s infinite;
}

.wserver-baseball-page .progress-bar-wrapper {
  height: 8px;
  background: #2a2a4a;
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 12px;
}

.wserver-baseball-page .progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #00ff9d, #00d4ff);
  transition: width 0.05s linear;
  border-radius: 999px;
}

.wserver-baseball-page .progress-bar.danger {
  background: linear-gradient(90deg, #ff3b5c, #ff6b6b);
}

/* Input Prompt */
.wserver-baseball-page .input-prompt {
  text-align: center;
}

.wserver-baseball-page .input-prompt p {
  margin: 4px 0;
  font-size: 15px;
}

.wserver-baseball-page .sub-prompt {
  color: #888;
  font-size: 13px !important;
}

/* Win / Game Over State */
.wserver-baseball-page .win-state,
.wserver-baseball-page .gameover-state {
  text-align: center;
  padding: 30px 20px;
}

.wserver-baseball-page .win-animation {
  font-size: 64px;
  animation: pop 0.6s ease;
}

@keyframes pop {
  0% { transform: scale(0.2); }
  70% { transform: scale(1.3); }
  100% { transform: scale(1); }
}

.wserver-baseball-page .win-title {
  color: #ffd700;
  font-size: 28px;
  margin: 12px 0 8px;
}

.wserver-baseball-page .win-message {
  font-size: 20px;
  color: #ccc;
  margin-bottom: 16px;
}

.wserver-baseball-page .winner-info {
  background: #2a2a4a;
  display: inline-block;
  padding: 10px 24px;
  border-radius: 12px;
  margin-bottom: 16px;
}

.wserver-baseball-page .next-info {
  color: #888;
  font-size: 14px;
}

.wserver-baseball-page .gameover-icon {
  font-size: 56px;
}

.wserver-baseball-page .gameover-title {
  color: #ff6b6b;
  font-size: 26px;
  margin: 8px 0;
}

.wserver-baseball-page .gameover-reason {
  color: #ccc;
  font-size: 16px;
}

/* Game Footer */
.wserver-baseball-page .game-footer {
  display: flex;
  justify-content: space-around;
  background: #1f1f3a;
  padding: 12px;
  border-top: 1px solid #2a2a4a;
}

.wserver-baseball-page .stat-item {
  text-align: center;
}

.wserver-baseball-page .stat-item .label {
  display: block;
  font-size: 12px;
  color: #888;
}

.wserver-baseball-page .stat-item .value {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
}

.wserver-baseball-page .status-active {
  color: #00ff9d !important;
}

/* Waiting Panel */
.wserver-baseball-page .waiting-panel {
  background: #16162b;
  border-radius: 16px;
  border: 1px solid #2a2a4a;
  padding: 16px 20px;
}

.wserver-baseball-page .waiting-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.wserver-baseball-page .waiting-header h3 {
  margin: 0;
  font-size: 18px;
  color: #00d4ff;
}

.wserver-baseball-page .count {
  color: #ffd700;
  font-weight: 700;
}

.wserver-baseball-page .update-hint {
  font-size: 12px;
  color: #666;
  background: #1f1f3a;
  padding: 2px 8px;
  border-radius: 10px;
}

.wserver-baseball-page .waiting-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 10px;
}

.wserver-baseball-page .waiting-card {
  background: #1f1f3a;
  border-radius: 10px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 15px;
  transition: all 0.2s;
}

.wserver-baseball-page .waiting-card.next-player {
  border: 2px solid #00ff9d;
  background: #0f2a1f;
}

.wserver-baseball-page .order {
  width: 24px;
  height: 24px;
  background: #2a2a4a;
  color: #ffd700;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
}

.wserver-baseball-page .masked-email {
  flex: 1;
  font-family: monospace;
  color: #ddd;
}

.wserver-baseball-page .next-badge {
  background: #00ff9d;
  color: #000;
  font-size: 10px;
  font-weight: 800;
  padding: 2px 8px;
  border-radius: 10px;
  letter-spacing: 0.5px;
}

.wserver-baseball-page .waiting-empty {
  text-align: center;
  padding: 30px 0;
  color: #888;
}

.wserver-baseball-page .empty-sub {
  font-size: 13px;
  margin-top: 4px;
}

/* Responsive */
@media (max-width: 1100px) {
  .wserver-baseball-page .main-layout {
    flex-direction: column;
  }
  .wserver-baseball-page .game-panel {
    min-height: auto;
  }
}

/* Answer Box */
.wserver-baseball-page .answer-box {
  margin: 16px auto;
  padding: 12px 24px;
  background: #2a2a4a;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  gap: 12px;
}

.wserver-baseball-page .answer-label {
  font-size: 14px;
  color: #aaa;
}

.wserver-baseball-page .answer-number {
  font-size: 28px;
  font-weight: 800;
  color: #ffd700;
  letter-spacing: 4px;
  font-family: 'Courier New', monospace;
}

/* Game Start Popup */
.wserver-baseball-page .game-start-popup-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 15, 35, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  border-radius: 16px;
  animation: fadeInOverlay 0.2s ease;
}

@keyframes fadeInOverlay {
  from { opacity: 0; }
  to { opacity: 1; }
}

.wserver-baseball-page .game-start-popup {
  background: linear-gradient(145deg, #1f1f3a, #16162b);
  border: 3px solid #ffd700;
  border-radius: 24px;
  padding: 42px 48px;
  text-align: center;
  max-width: 440px;
  width: 90%;
  box-shadow: 0 25px 70px rgba(0, 0, 0, 0.7),
              0 0 0 1px rgba(255, 215, 0, 0.2);
  animation: popInPopup 0.45s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
}

@keyframes popInPopup {
  from {
    transform: scale(0.5) translateY(40px);
    opacity: 0;
  }
  to {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

.wserver-baseball-page .popup-icon {
  font-size: 68px;
  margin-bottom: 12px;
  animation: bounce 1.2s infinite;
}

.wserver-baseball-page .game-start-popup h2 {
  color: #ffd700;
  font-size: 27px;
  margin: 0 0 10px;
  font-weight: 800;
}

.wserver-baseball-page .popup-player {
  font-size: 21px;
  color: #fff;
  margin: 6px 0 14px;
  font-weight: 600;
}

.wserver-baseball-page .popup-player strong {
  color: #00ff9d;
}

.wserver-baseball-page .popup-message {
  font-size: 19px;
  color: #ddd;
  line-height: 1.45;
  margin: 14px 0 8px;
}

.wserver-baseball-page .popup-sub {
  font-size: 14px;
  color: #888;
  margin-bottom: 26px;
}

.wserver-baseball-page .popup-close-btn {
  background: linear-gradient(90deg, #00d4ff, #00b8e0);
  color: #000;
  border: none;
  padding: 13px 36px;
  border-radius: 30px;
  font-weight: 700;
  font-size: 15.5px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 15px rgba(0, 212, 255, 0.3);
}

.wserver-baseball-page .popup-close-btn:hover {
  transform: translateY(-2px) scale(1.03);
  box-shadow: 0 6px 20px rgba(0, 212, 255, 0.4);
}
</style>